import React, { useState, useEffect } from "react";
import { FaFileAlt } from "react-icons/fa";
import { motion } from "framer-motion";

const allowedTypes = {
  cv: ["doc", "docx"],
  personal_statement: ["doc", "docx"],
  educational_doc: ["jpg", "jpeg", "pdf"],
  passport: ["jpg", "jpeg", "pdf"],
  additional_doc: ["jpg", "jpeg", "png", "pdf", "doc", "docx"],
};

const AddStudentDocument = () => {
  const [documentType, setDocumentType] = useState("");
  const [documentFile, setDocumentFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [allowedExtensions, setAllowedExtensions] = useState([]);

  // Get student ID and created_by from localStorage
  const studentId = (() => {
    try {
      return localStorage.getItem("student_id") || "";
    } catch {
      return "";
    }
  })();

  const created_by = (() => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      return user?.id || null;
    } catch {
      return null;
    }
  })();

  useEffect(() => {
    if (documentType) {
      setAllowedExtensions(allowedTypes[documentType] || []);
      setDocumentFile(null); // reset file when type changes
    } else {
      setAllowedExtensions([]);
    }
  }, [documentType]);

  const validateFile = (file) => {
    const ext = file.name.split(".").pop().toLowerCase();
    return allowedExtensions.includes(ext);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setError("");
    if (file && !validateFile(file)) {
      setDocumentFile(null);
      setError(`❌ Invalid file type. Allowed: ${allowedExtensions.join(", ").toUpperCase()}`);
      return;
    }
    setDocumentFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    if (!studentId || !documentType || !documentFile || !created_by) {
      setError("Please fill in all required fields.");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("student_id", studentId);
      formData.append("document_type", documentType);
      formData.append("created_by", created_by);
      formData.append("document_file", documentFile);

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/add-student-document`,
        {
          method: "POST",
          body: formData,
           headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`, // attach token
          },
        }
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to upload document.");
      }

      await res.json();
      setSuccessMsg("✅ Document uploaded successfully!");
      setDocumentType("");
      setDocumentFile(null);
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="max-w-md mx-auto p-4 sm:p-6 space-y-4 bg-white rounded-xl shadow-sm border border-gray-200"
    >
      <h2 className="text-xl font-bold text-center text-gray-800 mb-2">
        📁 Upload Document
      </h2>

      {/* Document Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Document Type
        </label>
        <div className="relative">
          <select
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
          >
            <option value="">-- Select Document Type --</option>
            <option value="cv">CV</option>
            <option value="personal_statement">Personal Statement</option>
            <option value="educational_doc">Educational Document</option>
            <option value="additional_doc">Additional Document</option>
            <option value="passport">Passport</option>
          </select>
          <FaFileAlt className="absolute left-3 top-2.5 text-gray-500" size={16} />
        </div>
      </div>

      {/* File Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Upload File
        </label>
        <input
          key={documentType}
          type="file"
          onChange={handleFileChange}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 file:bg-blue-600 file:text-white file:border-none file:px-3 file:py-1.5 file:rounded-md file:cursor-pointer file:text-sm transition"
          accept={allowedExtensions.map((ext) => "." + ext).join(",")}
        />
        {allowedExtensions.length > 0 && (
          <p className="text-xs text-gray-500 mt-1.5">
            Allowed types: {allowedExtensions.join(", ").toUpperCase()}
          </p>
        )}
      </div>

      {/* Submit Button */}
      <motion.button
        whileTap={{ scale: 0.98 }}
        type="submit"
        disabled={loading}
        className={`w-full py-2.5 text-sm rounded-lg font-medium transition ${
          loading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
        }`}
      >
        {loading ? "Uploading..." : "Upload Document"}
      </motion.button>

      {/* Feedback */}
      {error && <p className="text-red-600 text-xs text-center">{error}</p>}
      {successMsg && <p className="text-green-600 text-xs text-center">{successMsg}</p>}
    </motion.form>
  );
};

export default AddStudentDocument;